import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImageIcon, XIcon, EyeIcon } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ATMTextField from "../../../Atom/ATMTextField/ATMTextField";
import {
  useAddVendorDealMutation,
  useGetAllServicesQuery,
  useGetVendorLocationQuery,
  useUploadImageMutation,
} from "../../../../redux/service/vendorService";
import { toast } from "sonner";
import ATMLoadingButton from "../../../Atom/ATMLoadingButton/ATMLoadingButton";

  const categories = [
    "Automotive Services & Products",
    "Art, Crafts & Collectibles",
    "Baby Care",
    "Bakery",
    "Books, Stationery & Toys",
    "Clothing",
    "Consultants",
    "Dentist",
    "Electronics",
    "Estate Agents",
    "Fashion, Apparel & Accessories",
    "Food",
    "Furniture",
    "Groceries",
    "Health, Wellness & Fitness",
    "Home, Living & Kitchen",
    "Others",
    "Personal Care",
    "Pet Care Services & Supplies",
    "Professional & Business Services",
    "Rent & Hire",
    "Restaurants",
    "Sports & Outdoors",
    "Other Services & Consultations",
  ];

const AddDealForm = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [vendorServices, setVendorServices] = useState([]);
  const [vendorLocationData, setVendorLocationData] = useState([]);

  const [uploadDealImage] = useUploadImageMutation();
  const vendorId = localStorage.getItem("vendor_id");
  const {
    data: LocationData,
    isLoading: LocationDataIsLoading,
    isFetching: LocationDataIsFetching,
  } = useGetVendorLocationQuery(vendorId, { skip: !vendorId });
  const { data, isLoading, isFetching } = useGetAllServicesQuery();
  const [addDeal, addDealInfo] = useAddVendorDealMutation();

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setVendorServices(data?.data);
    }
  }, [data, isLoading, isFetching]);

  useEffect(() => {
    if (!LocationDataIsLoading && !LocationDataIsFetching) {
      setVendorLocationData(LocationData);
    }
  }, [LocationData, LocationDataIsLoading, LocationDataIsFetching]);

  // Validation schema
  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    selectedService: Yup.string(),
    category: Yup.string().required("Category is required"),
    endDateTime: Yup.date()
      .required("End date and time is required")
      .min(new Date(), "End date must be in the future"),
    actualPrice: Yup.number()
      .required("Price is required")
      .positive("Price must be positive"),
    dealPrice: Yup.number()
      .required("Deal price is required")
      .positive("Deal price must be positive")
      .test(
        "is-less-than-actual",
        "Deal price must be less than actual price",
        function (value) {
          return value <= this.parent.actualPrice;
        }
      ),
    availableDeals: Yup.number()
      .required("Quantity is required")
      .positive("Quantity must be positive")
      .integer("Quantity must be a whole number"),
    address: Yup.string().required("Address is required"),
    displayPurchaseButton: Yup.boolean(),
  });

  const handleImageUpload = async (files) => {
    const newPreviews = Array.from(files).map((file) => ({
      thumbnail: URL.createObjectURL(file),
      compressed: URL.createObjectURL(file),
      file, // Keep the file reference for upload
    }));

    // Set previews immediately
    setImages([...images, ...newPreviews]);

    // Upload each file and update Formik values
    for (const preview of newPreviews) {
      const formData = new FormData();
      formData.append("images", preview?.file);
      formData.append("model_name", "CreateDeal");
      try {
        const response = await uploadDealImage(formData).unwrap();

        // Check for successful response based on your actual API structure
        if (
          response.data &&
          response.data[0]?.thumbnail &&
          response.data[0]?.compressed
        ) {
          // Update the preview with actual URLs
          setImages((prev) =>
            prev.map((img) =>
              img.file === preview.file
                ? {
                    thumbnail: response.data[0].thumbnail,
                    compressed: response.data[0].compressed,
                    file: null, // Clear file reference after upload
                  }
                : img
            )
          );

          toast.success(response.message || "Image uploaded successfully!");
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        toast.error(error.message || "Image upload failed");
        setImages((prev) => prev.filter((img) => img.file !== preview.file));
      }
    }
  };

  const removeImage = (index) => {
    if (images[index].file) {
      URL.revokeObjectURL(images[index].thumbnail);
      URL.revokeObjectURL(images[index].compressed);
    }
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);

  };

  const handleSubmit = async (values) => {
    const selectedaddress = LocationData?.find(
      (service) => service.uuid === values?.address
    );
    const [date, time] = values?.endDateTime.split("T");
    const formData = {
      deal_title: values?.title,
      deal_description: values?.description,
      service: values?.selectedService,
      category: values?.category,
      end_date: date,
      end_time: time,
      actual_price: values?.actualPrice,
      deal_price: values?.dealPrice,
      available_deals: values?.availableDeals,
      uploaded_images: images?.map((el) => {
        return {
          thumbnail: el?.thumbnail,
          compressed: el?.compressed,
        };
      }),
      location_house_no: selectedaddress?.house_no_building_name,
      location_road_name: selectedaddress?.road_name_area_colony,
      location_country: selectedaddress?.country,
      location_state: selectedaddress?.state,
      location_city: selectedaddress?.city,
      location_pincode: selectedaddress?.pincode,
      vendor_kyc: vendorId,
      latitude: selectedaddress?.latitude,
      longitude: selectedaddress?.longitude,
    };
    try {
      await addDeal(formData).unwrap();
      toast.success("Deal added successfully");
      navigate('/vendor/dashboard')
    } catch {
      toast.error("Failed to add Deal.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="border-b border-gray-200">
            <div className="px-8 py-6">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    Create New Deal
                  </h1>
                  <p className="text-gray-600">
                    Fill in the details below to create an attractive deal
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Formik
            initialValues={{
              title: "",
              description: "",
              selectedService: "",
              category: "",
              endDateTime: "",
              actualPrice: "",
              dealPrice: "",
              availableDeals: "",
              address: "",
              displayPurchaseButton: true,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue, handleBlur }) => {
              return (
                <Form className="px-8 py-6">
                  <div className="space-y-10">
                    {/* Basic Information Section */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                          1
                        </span>
                        Basic Information
                      </h2>
                      <div className="grid gap-6 max-w-3xl">
                        <ATMTextField
                          name="title"
                          label="Deal Title"
                          required
                          placeholder="Enter a catchy title for your deal"
                        />
                        <ATMTextField
                          required
                          name="description"
                          label="Description"
                          placeholder="Describe your deal and its benefits"
                          as="textarea"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Service (Optional)
                            </label>
                             <Field
                             disabled={!vendorServices?.length}
                              as="select"
                              name="selectedService"
                              onChange={(e) => {
                                const serviceId = Number(e.target.value);
                                const selectedService = vendorServices?.find(
                                  (service) => service.id === serviceId
                                );
                                setFieldValue("selectedService", serviceId);
                                if (selectedService) {
                                  setFieldValue(
                                    "actualPrice",
                                    selectedService.price
                                  );
                                  setFieldValue(
                                    "category",
                                    selectedService.category
                                  );
                                }
                              }}
                              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                              <option value="">Select a service</option>
                              {vendorServices?.map((service) => (
                                <option key={service.id} value={service.id}>
                                  {service.name} - ₹{service.price}
                                </option>
                              ))}
                            </Field> 
                            {!vendorServices?.length && <span onClick={()=>{navigate('/vendor/services')}} className="text-red text-xs text-red-700 font-medium cursor-pointer">No services added. Please add one before creating a deal.</span> }

                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Category
                            </label>
                            <Field
                              disabled={values?.selectedService}
                              as="select"
                              name="category"
                              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                              <option value="">Select a category</option>
                              {categories.map((category) => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="category"
                              component="p"
                              className="mt-2 text-sm text-red-600"
                            />
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Images Section */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                          2
                        </span>
                        Deal Images
                      </h2>
                      <div className="bg-gray-50 rounded-xl p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {images.map((image, index) => (
                            <div
                              key={index}
                              className="relative aspect-square rounded-lg overflow-hidden bg-white shadow-sm"
                            >
                              <img
                                src={image.compressed}
                                alt={`Deal image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  removeImage(index, values, setFieldValue)
                                }
                                className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                              >
                                <XIcon className="h-4 w-4 text-gray-600" />
                              </button>
                            </div>
                          ))}
                         {images?.length < 5 &&  <label className="relative aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-500 cursor-pointer flex items-center justify-center bg-white transition-colors">
                            <div className="text-center p-4">
                              <ImageIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                              <span className="block text-sm font-medium text-gray-600">
                                Add Image
                              </span>
                              <span className="block text-xs text-gray-500 mt-1">
                                Up to 5 images
                              </span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) =>
                                e.target.files &&
                                handleImageUpload(
                                  e.target.files,
                                  values,
                                  setFieldValue
                                )
                              }
                            />
                          </label>}
                        </div>
                        {images.length === 0 && (
                          <p className="mt-3 text-sm text-red-600">
                            At least one image is required
                          </p>
                        )}
                      </div>
                    </section>

                    {/* Pricing & Timing Section */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                          3
                        </span>
                        Pricing & Timing
                      </h2>
                      <div className="grid gap-6 max-w-3xl">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="relative">
                            <ATMTextField
                              name="actualPrice"
                              label="Regular Price"
                              type="number"
                              required
                              disabled={!!values.selectedService}
                              extraClassName={
                                values.selectedService ? "bg-gray-50" : ""
                              }
                              placeholder="0.00"
                            />
                          </div>
                          <div className="relative">
                            <ATMTextField
                              name="dealPrice"
                              label="Deal Price"
                              type="number"
                              required
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Deal End Date & Time
                          </label>
                          <Field
                            name="endDateTime"
                            type="datetime-local"
                            value={values?.endDateTime}
                            min={new Date().toISOString().slice(0, 16)}
                            onChange={(e)=>{
                               setFieldValue('endDateTime',e.target.value)
                               e.target.blur();
                            }}
                            handleBlur={handleBlur}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                          <p className="mt-2 text-sm text-gray-500">
                            Select when the deal will expire
                          </p>
                          <ErrorMessage
                            name="endDateTime"
                            component="p"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>
                      </div>
                    </section>

                    {/* Additional Details Section */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                        <span className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                          4
                        </span>
                        Additional Details
                      </h2>
                      <div className="space-y-6 max-w-3xl">
                        <div className="bg-gray-50 rounded-xl p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">
                                Display Purchase Button
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                Show the "Buy Now" button on the deal page
                              </p>
                            </div>
                            <Field
                              name="displayPurchaseButton"
                              type="checkbox"
                              className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                            >
                              {({ field }) => (
                                <button
                                  type="button"
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                    field.value
                                      ? "bg-orange-500"
                                      : "bg-gray-200"
                                  }`}
                                  onClick={() => {
                                    setFieldValue(
                                      "displayPurchaseButton",
                                      !field.value
                                    );
                                  }}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                      field.value
                                        ? "translate-x-5"
                                        : "translate-x-0"
                                    }`}
                                  />
                                </button>
                              )}
                            </Field>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <ATMTextField
                            name="availableDeals"
                            label="Available Quantity"
                            type="number"
                            required
                            min="1"
                            placeholder="Enter number of available deals"
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Deal Location
                            </label>
                            <Field
                              as="select"
                              name="address"
                              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                              <option value="">Select an address</option>
                              {vendorLocationData?.map((address, index) => (
                                <option key={index} value={address?.uuid}>
                                  {`${address?.house_no_building_name},${address?.city},${address?.state},${address?.country},${address?.pincode}`}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="address"
                              component="p"
                              className="mt-2 text-sm text-red-600"
                            />
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-10 border-t pt-6">
                    {/* <button
                      type="submit"
                      className="w-full md:w-auto px-8 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium transition-colors"
                      disabled={images.length === 0}
                    >
                      Create Deal
                    </button> */}
                     <ATMLoadingButton
                     label='Create Deal'
                     type='submit'
                     disabled={images.length === 0}
                     loading={addDealInfo?.isLoading || addDealInfo?.isFetching }
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default AddDealForm;
